import localeData from './locales.json';

export const DEFAULT_LANG = 'en-US';
export const LANGUAGE_STORAGE_KEY = 'pdf911:language';

export type LocaleDefinition = {
  code: string;
  hreflang: string;
  prefix: string;
  locale: string;
  direction?: 'ltr' | 'rtl';
  name: string;
  homeTitle: string;
  homeDescription: string;
  homeH1: string;
  homeLead: string;
  genericLead: string;
  genericSteps: string[];
  genericFaqs: Array<[string, string]>;
};

export const SUPPORTED_LOCALES = localeData as LocaleDefinition[];
export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LOCALES.map((locale) => locale.code);

export function matchSupportedLanguage(languageTag: string | undefined | null): string | null {
  if (!languageTag) return null;
  const normalized = languageTag.toLowerCase().replace(/_/g, '-');
  const exact = SUPPORTED_LOCALES.find((locale) => locale.code.toLowerCase() === normalized);
  if (exact) return exact.code;
  const base = normalized.split('-')[0];
  const baseMatch = SUPPORTED_LOCALES.find((locale) => locale.code.toLowerCase().split('-')[0] === base);
  return baseMatch?.code ?? null;
}

export function getStoredLanguage(): string | null {
  try {
    return matchSupportedLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function setStoredLanguage(lang: string): void {
  const supported = matchSupportedLanguage(lang) ?? DEFAULT_LANG;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, supported);
  } catch {
    // Private browsing modes can reject localStorage writes.
  }
}

export function detectDeviceLanguage(): string {
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of languages) {
    const supported = matchSupportedLanguage(language);
    if (supported) return supported;
  }
  return DEFAULT_LANG;
}

export function currentClientLanguage(): string {
  const pathPrefix = window.location.pathname.split('/').filter(Boolean)[0];
  const candidates = [
    pathPrefix,
    document.documentElement.lang,
    getStoredLanguage(),
    ...(navigator.languages ?? []),
    navigator.language,
  ];
  for (const candidate of candidates) {
    const supported = matchSupportedLanguage(candidate);
    if (supported) return supported;
  }
  return DEFAULT_LANG;
}

export function languageDirection(languageTag: string | undefined | null): 'ltr' | 'rtl' {
  const supported = matchSupportedLanguage(languageTag) ?? DEFAULT_LANG;
  return SUPPORTED_LOCALES.find((locale) => locale.code === supported)?.direction ?? 'ltr';
}
